<!-- Sticky category bar (sits directly below the sticky header h-16 = top-16) -->
@if (products.length) {
  <div class="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
    <div class="max-w-3xl mx-auto px-4 py-2 flex flex-wrap gap-2">
      @for (category of products; track category) {
        <tt-button
          [variant]="
            activeCategoryId() === category.id ? 'primary' : 'secondary'
          "
          (clicked)="scrolltoElement(category.id)"
        >
          {{ category.description }}
        </tt-button>
      }
    </div>
  </div>
}

<div class="max-w-3xl mx-auto px-4 py-6">
  <p [innerHTML]="textService.get('products.top_text') | async"></p>

  <form [formGroup]="form">
    @if (tableError) {
      <tt-alert
        variant="danger"
        [message]="(textService.get('products.table_error') | async) ?? ''"
      />
    }

    <!-- Product groups -->
    @for (productGroup of products; track productGroup) {
      <div
        [id]="productGroup.id"
        class="bg-white rounded-xl shadow-sm border border-gray-200 mb-6"
      >
        <div class="px-5 py-4 border-b border-gray-100">
          <h5 class="font-semibold text-gray-800 text-base">
            {{ productGroup.description }}
          </h5>
        </div>
        @if (!tableError) {
          <div class="divide-y divide-gray-100">
            @for (product of productGroup.products; track product) {
              <ng-container [formGroupName]="product.id">
                <div
                  [id]="'product-row-' + product.id"
                  class="px-5 py-4"
                  [class.bg-blue-50]="product.id === currentProductId"
                >
                  <!-- Top row: image + name (all sizes) + price and controls on desktop -->
                  <div class="flex items-center gap-4">
                    @if (product.images[0]) {
                      <img
                        [id]="'product-img-' + product.id"
                        (click)="previewImage(product.images[0]['image'])"
                        [src]="product.images[0]['image']"
                        class="w-16 h-16 object-contain rounded-lg cursor-pointer flex-shrink-0"
                        alt=""
                      />
                    }
                    <div class="flex-1 min-w-0">
                      <p class="font-medium text-gray-900 text-sm">
                        {{ product.description }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ product.short_description }}
                      </p>
                    </div>
                    <!-- Price (desktop only) -->
                    <span
                      class="hidden sm:block text-sm font-medium text-gray-700 w-20 text-right flex-shrink-0"
                    >
                      {{ product.price | ttcurrency }}
                    </span>
                    <!-- Quantity controls (desktop only) -->
                    <div
                      class="hidden sm:flex items-center gap-1 flex-shrink-0"
                    >
                      <tt-button
                        variant="outline"
                        (clicked)="decrement(product.id)"
                        >−</tt-button
                      >
                      <input
                        [value]="getProductQuantity(product.id)"
                        type="text"
                        class="w-10 text-center text-sm border border-gray-300 rounded-lg py-1.5 bg-gray-50"
                        readonly
                      />
                      <tt-button
                        variant="primary"
                        (clicked)="increment(product.id)"
                        >+</tt-button
                      >
                    </div>
                  </div>
                  <!-- Bottom row: price + controls (mobile only) -->
                  <div class="flex items-center mt-2 sm:hidden">
                    <span class="text-sm font-medium text-gray-700 flex-1">{{
                      product.price | ttcurrency
                    }}</span>
                    <div class="flex items-center gap-1">
                      <tt-button
                        variant="outline"
                        (clicked)="decrement(product.id)"
                      >
                        −
                      </tt-button>
                      <input
                        [value]="getProductQuantity(product.id)"
                        type="text"
                        class="w-10 text-center text-sm border border-gray-300 rounded-lg py-1.5 bg-gray-50"
                        readonly
                      />
                      <tt-button
                        variant="primary"
                        (clicked)="increment(product.id)"
                      >
                        +
                      </tt-button>
                    </div>
                  </div>
                </div>

                <!-- Option modal (inline) -->
                @if (showOptionModal && product.id === currentProductId) {
                  <div
                    class="bg-gray-50 px-3 py-4 border-t border-gray-100"
                    formGroupName="options"
                  >
                    @for (question of product.questions; track question) {
                      <p class="font-medium text-sm text-gray-700 mb-1">
                        {{ question.question }}
                      </p>
                      @if (question.type === "SINGLE_CHOICE") {
                        <tt-option-group
                          [formControlName]="question.id"
                          [options]="getAnswerOptions(question.answers)"
                          class="mb-3 block"
                        />
                      }
                      @if (question.type === "OPEN") {
                        <input
                          [formControlName]="question.id"
                          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3"
                        />
                      }
                    }
                    <tt-button
                      variant="primary"
                      (clicked)="addProductWithQuestions(product.id)"
                      [disabled]="!form.get(product.id)!.get('options')!.valid"
                    >
                      {{ textService.get("products.add") | async }}
                    </tt-button>
                  </div>
                }
              </ng-container>
            }
          </div>
        }
      </div>
    }

    <!-- Fixed checkout button -->
    <div class="fixed bottom-4 right-4 z-20">
      <tt-button
        #cartButton
        variant="primary"
        (clicked)="confirmOrder()"
        [disabled]="!shouldEnableActionButtons()"
      >
        {{ cartStore.totalPrice() | async | ttcurrency }}
        {{ textService.get("products.submit") | async }}
      </tt-button>
    </div>
  </form>
</div>

<!-- Image preview modal -->
@if (showImageModal) {
  <tt-modal
    [dismissableOnBackdropClick]="true"
    (modalClose)="showImageModal = false"
  >
    <img
      body
      [src]="image"
      class="max-w-full max-h-[80vh] object-contain rounded-lg"
      alt=""
    />
  </tt-modal>
}
